import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Users, Filter, UserPlus, Tag, Layers, Mail,
  Plus, Search, MoreVertical, Edit2, Trash2, Eye,
  TrendingUp, UserCheck, Building, Phone, Globe,
  ChevronDown, Save, X, Check, RefreshCw, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const CRMPanel = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [segments, setSegments] = useState([]);
  const [groups, setGroups] = useState([]);
  const [forms, setForms] = useState([]);
  
  // Modal states
  const [showContactModal, setShowContactModal] = useState(false);
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Form states
  const [contactForm, setContactForm] = useState({
    email: '', first_name: '', last_name: '', phone: '', company: '',
    job_title: '', tags: '', lifecycle_stage: 'subscriber', notes: ''
  });
  const [segmentForm, setSegmentForm] = useState({ name: '', description: '' });
  const [groupForm, setGroupForm] = useState({ name: '', description: '', color: '#8b5cf6' });
  
  const token = localStorage.getItem('token');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: TrendingUp },
    { id: 'contacts', name: 'Contacts', icon: Users },
    { id: 'segments', name: 'Segments', icon: Filter },
    { id: 'groups', name: 'Groups', icon: Layers },
    { id: 'forms', name: 'Forms', icon: FileText },
  ];

  const lifecycleStages = [
    { value: 'subscriber', label: 'Subscriber', color: 'bg-blue-500' },
    { value: 'lead', label: 'Lead', color: 'bg-yellow-500' },
    { value: 'opportunity', label: 'Opportunity', color: 'bg-purple-500' },
    { value: 'customer', label: 'Customer', color: 'bg-green-500' },
  ];

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [statsRes, contactsRes, segmentsRes, groupsRes, formsRes] = await Promise.all([
        fetch(`${API_URL}/api/websites/${websiteId}/crm-stats`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/contacts`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/segments`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/groups`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/forms`, { headers }),
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (contactsRes.ok) setContacts(await contactsRes.json());
      if (segmentsRes.ok) setSegments(await segmentsRes.json());
      if (groupsRes.ok) setGroups(await groupsRes.json());
      if (formsRes.ok) setForms(await formsRes.json());
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load CRM data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteId]);

  // Contact CRUD
  const saveContact = async () => {
    try {
      const method = editingItem ? 'PUT' : 'POST';
      const url = editingItem 
        ? `${API_URL}/api/websites/${websiteId}/contacts/${editingItem.id}`
        : `${API_URL}/api/websites/${websiteId}/contacts`;
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...contactForm,
          tags: contactForm.tags ? contactForm.tags.split(',').map(t => t.trim()) : []
        })
      });
      
      if (response.ok) {
        toast.success(editingItem ? 'Contact updated!' : 'Contact created!');
        setShowContactModal(false);
        setEditingItem(null);
        setContactForm({ email: '', first_name: '', last_name: '', phone: '', company: '', job_title: '', tags: '', lifecycle_stage: 'subscriber', notes: '' });
        fetchAllData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save contact');
      }
    } catch (error) {
      toast.error('Failed to save contact');
    }
  };

  const deleteContact = async (contactId) => {
    if (!confirm('Delete this contact?')) return;
    try {
      await fetch(`${API_URL}/api/websites/${websiteId}/contacts/${contactId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Contact deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  // Segment CRUD
  const saveSegment = async () => {
    try {
      const response = await fetch(`${API_URL}/api/websites/${websiteId}/segments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(segmentForm)
      });
      if (response.ok) {
        toast.success('Segment created!');
        setShowSegmentModal(false);
        setSegmentForm({ name: '', description: '' });
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to create segment');
    }
  };

  // Group CRUD
  const saveGroup = async () => {
    try {
      const response = await fetch(`${API_URL}/api/websites/${websiteId}/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(groupForm)
      });
      if (response.ok) {
        toast.success('Group created!');
        setShowGroupModal(false);
        setGroupForm({ name: '', description: '', color: '#8b5cf6' });
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to create group');
    }
  };

  const filteredContacts = contacts.filter(c => 
    !searchQuery || 
    c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.last_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-violet-500/20 to-purple-500/20 rounded-xl p-6 border border-violet-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Total Contacts</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.total_contacts || 0}</p>
            </div>
            <Users className="w-10 h-10 text-violet-400" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 rounded-xl p-6 border border-emerald-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Active Contacts</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.active_contacts || 0}</p>
            </div>
            <UserCheck className="w-10 h-10 text-emerald-400" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl p-6 border border-blue-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Subscribers</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.subscribers || 0}</p>
            </div>
            <Mail className="w-10 h-10 text-blue-400" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-xl p-6 border border-amber-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Segments</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.total_segments || 0}</p>
            </div>
            <Filter className="w-10 h-10 text-amber-400" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Recent Contacts */}
        <div className="bg-zinc-900 rounded-xl border border-white/10 p-6">
          <h3 className="text-lg font-semibold mb-4">Recent Contacts</h3>
          <div className="space-y-3">
            {(stats?.recent_contacts || []).map(contact => (
              <div key={contact.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-500/20 rounded-full flex items-center justify-center">
                    <span className="text-violet-400 font-medium">
                      {(contact.first_name?.[0] || contact.email?.[0] || '?').toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">{contact.first_name} {contact.last_name}</p>
                    <p className="text-sm text-zinc-400">{contact.email}</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${lifecycleStages.find(s => s.value === contact.lifecycle_stage)?.color || 'bg-zinc-500'} text-white`}>
                  {contact.lifecycle_stage}
                </span>
              </div>
            ))}
            {(!stats?.recent_contacts || stats.recent_contacts.length === 0) && (
              <p className="text-center text-zinc-500 py-8">No contacts yet</p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-zinc-900 rounded-xl border border-white/10 p-6">
          <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => { setEditingItem(null); setShowContactModal(true); }}
              className="p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-3 transition-colors"
            >
              <UserPlus className="w-5 h-5 text-violet-400" />
              <span>Add Contact</span>
            </button>
            <button
              onClick={() => setShowSegmentModal(true)}
              className="p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-3 transition-colors"
            >
              <Filter className="w-5 h-5 text-emerald-400" />
              <span>Create Segment</span>
            </button>
            <button
              onClick={() => setShowGroupModal(true)}
              className="p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-3 transition-colors"
            >
              <Layers className="w-5 h-5 text-blue-400" />
              <span>Create Group</span>
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className="p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-3 transition-colors"
            >
              <Users className="w-5 h-5 text-amber-400" />
              <span>View All Contacts</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContacts = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white w-64"
            />
          </div>
          <select className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white">
            <option value="">All Stages</option>
            {lifecycleStages.map(stage => (
              <option key={stage.value} value={stage.value}>{stage.label}</option>
            ))}
          </select>
        </div>
        <Button onClick={() => { setEditingItem(null); setContactForm({ email: '', first_name: '', last_name: '', phone: '', company: '', job_title: '', tags: '', lifecycle_stage: 'subscriber', notes: '' }); setShowContactModal(true); }} className="bg-violet-500">
          <Plus className="w-4 h-4 mr-2" />Add Contact
        </Button>
      </div>

      <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-800">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Contact</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Company</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Phone</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Stage</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Tags</th>
              <th className="text-right px-6 py-4 text-sm font-medium text-zinc-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {filteredContacts.map(contact => (
              <tr key={contact.id} className="hover:bg-zinc-800/50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-violet-500/20 rounded-full flex items-center justify-center">
                      <span className="text-violet-400 font-medium">
                        {(contact.first_name?.[0] || contact.email?.[0] || '?').toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{contact.first_name} {contact.last_name}</p>
                      <p className="text-sm text-zinc-400">{contact.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {contact.company && (
                    <div className="flex items-center gap-2">
                      <Building className="w-4 h-4 text-zinc-500" />
                      <span>{contact.company}</span>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4">
                  {contact.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-zinc-500" />
                      <span>{contact.phone}</span>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs text-white ${lifecycleStages.find(s => s.value === contact.lifecycle_stage)?.color || 'bg-zinc-500'}`}>
                    {contact.lifecycle_stage}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {(contact.tags || []).slice(0, 3).map((tag, i) => (
                      <span key={i} className="px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded text-xs">{tag}</span>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => {
                        setEditingItem(contact);
                        setContactForm({
                          email: contact.email || '',
                          first_name: contact.first_name || '',
                          last_name: contact.last_name || '',
                          phone: contact.phone || '',
                          company: contact.company || '',
                          job_title: contact.job_title || '',
                          tags: (contact.tags || []).join(', '),
                          lifecycle_stage: contact.lifecycle_stage || 'subscriber',
                          notes: contact.notes || ''
                        });
                        setShowContactModal(true);
                      }}
                      className="p-2 hover:bg-zinc-700 rounded-lg"
                    >
                      <Edit2 className="w-4 h-4 text-zinc-400" />
                    </button>
                    <button onClick={() => deleteContact(contact.id)} className="p-2 hover:bg-red-500/20 rounded-lg">
                      <Trash2 className="w-4 h-4 text-zinc-400 hover:text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredContacts.length === 0 && (
          <div className="text-center py-12 text-zinc-500">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No contacts found</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderSegments = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Segments</h3>
        <Button onClick={() => setShowSegmentModal(true)} className="bg-violet-500">
          <Plus className="w-4 h-4 mr-2" />Create Segment
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {segments.map(segment => (
          <div key={segment.id} className="bg-zinc-900 rounded-xl border border-white/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <Filter className="w-8 h-8 text-violet-400" />
              <span className="text-sm text-zinc-400">{segment.contact_count || 0} contacts</span>
            </div>
            <h4 className="font-semibold mb-2">{segment.name}</h4>
            <p className="text-sm text-zinc-400 mb-4">{segment.description || 'No description'}</p>
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <span className={segment.is_dynamic ? 'text-emerald-400' : 'text-zinc-400'}>
                {segment.is_dynamic ? 'Dynamic' : 'Static'}
              </span>
            </div>
          </div>
        ))}
        {segments.length === 0 && (
          <div className="col-span-3 text-center py-12 text-zinc-500 bg-zinc-900 rounded-xl border border-white/10">
            <Filter className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No segments yet</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderGroups = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Groups</h3>
        <Button onClick={() => setShowGroupModal(true)} className="bg-violet-500">
          <Plus className="w-4 h-4 mr-2" />Create Group
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {groups.map(group => (
          <div key={group.id} className="bg-zinc-900 rounded-xl border border-white/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${group.color}20` }}>
                <Layers className="w-5 h-5" style={{ color: group.color }} />
              </div>
              <span className="text-sm text-zinc-400">{group.member_count || 0} members</span>
            </div>
            <h4 className="font-semibold mb-2">{group.name}</h4>
            <p className="text-sm text-zinc-400">{group.description || 'No description'}</p>
          </div>
        ))}
        {groups.length === 0 && (
          <div className="col-span-3 text-center py-12 text-zinc-500 bg-zinc-900 rounded-xl border border-white/10">
            <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No groups yet</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderForms = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Contact Forms</h3>
        <Button className="bg-violet-500">
          <Plus className="w-4 h-4 mr-2" />Create Form
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {forms.map(form => (
          <div key={form.id} className="bg-zinc-900 rounded-xl border border-white/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <FileText className="w-8 h-8 text-blue-400" />
              <span className="text-sm text-zinc-400">{form.submissions_count || 0} submissions</span>
            </div>
            <h4 className="font-semibold mb-2">{form.name}</h4>
            <p className="text-sm text-zinc-400 mb-4">{form.description || 'No description'}</p>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 rounded-full text-xs ${form.is_active ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                {form.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        ))}
        {forms.length === 0 && (
          <div className="col-span-2 text-center py-12 text-zinc-500 bg-zinc-900 rounded-xl border border-white/10">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No forms yet</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return renderDashboard();
      case 'contacts': return renderContacts();
      case 'segments': return renderSegments();
      case 'groups': return renderGroups();
      case 'forms': return renderForms();
      default: return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button onClick={() => navigate(`/edit/${websiteId}`)} variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Editor
          </Button>
          <div className="h-6 w-px bg-zinc-700" />
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-400" />
            <h1 className="text-lg font-semibold text-white">CRM & Contacts</h1>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-zinc-900 border-r border-white/10 min-h-[calc(100vh-73px)]">
          <div className="p-4 space-y-1">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === tab.id 
                      ? 'bg-emerald-500/20 text-emerald-400' 
                      : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm">{tab.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-8">{renderContent()}</div>
      </div>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 border border-white/10">
            <h3 className="text-xl font-bold mb-6">{editingItem ? 'Edit Contact' : 'Add Contact'}</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">First Name</label>
                  <input type="text" value={contactForm.first_name} onChange={(e) => setContactForm({...contactForm, first_name: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="John" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Last Name</label>
                  <input type="text" value={contactForm.last_name} onChange={(e) => setContactForm({...contactForm, last_name: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="Doe" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <input type="email" value={contactForm.email} onChange={(e) => setContactForm({...contactForm, email: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="john@example.com" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input type="tel" value={contactForm.phone} onChange={(e) => setContactForm({...contactForm, phone: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="+1 234 567 890" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Company</label>
                  <input type="text" value={contactForm.company} onChange={(e) => setContactForm({...contactForm, company: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="Acme Inc." />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Job Title</label>
                  <input type="text" value={contactForm.job_title} onChange={(e) => setContactForm({...contactForm, job_title: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="Marketing Manager" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Lifecycle Stage</label>
                  <select value={contactForm.lifecycle_stage} onChange={(e) => setContactForm({...contactForm, lifecycle_stage: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white">
                    {lifecycleStages.map(stage => (
                      <option key={stage.value} value={stage.value}>{stage.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Tags (comma-separated)</label>
                <input type="text" value={contactForm.tags} onChange={(e) => setContactForm({...contactForm, tags: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="vip, newsletter, premium" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Notes</label>
                <textarea value={contactForm.notes} onChange={(e) => setContactForm({...contactForm, notes: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none" rows={3} placeholder="Add notes about this contact..." />
              </div>
              <div className="pt-4 flex gap-3">
                <Button onClick={() => { setShowContactModal(false); setEditingItem(null); }} variant="outline" className="flex-1 border-zinc-700">Cancel</Button>
                <Button onClick={saveContact} className="flex-1 bg-emerald-500">{editingItem ? 'Update' : 'Create'} Contact</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Segment Modal */}
      {showSegmentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-lg p-6 border border-white/10">
            <h3 className="text-xl font-bold mb-6">Create Segment</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Segment Name *</label>
                <input type="text" value={segmentForm.name} onChange={(e) => setSegmentForm({...segmentForm, name: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="VIP Customers" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea value={segmentForm.description} onChange={(e) => setSegmentForm({...segmentForm, description: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none" rows={3} placeholder="Describe this segment..." />
              </div>
              <div className="pt-4 flex gap-3">
                <Button onClick={() => setShowSegmentModal(false)} variant="outline" className="flex-1 border-zinc-700">Cancel</Button>
                <Button onClick={saveSegment} className="flex-1 bg-emerald-500">Create Segment</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Group Modal */}
      {showGroupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-lg p-6 border border-white/10">
            <h3 className="text-xl font-bold mb-6">Create Group</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Group Name *</label>
                <input type="text" value={groupForm.name} onChange={(e) => setGroupForm({...groupForm, name: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="Premium Members" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea value={groupForm.description} onChange={(e) => setGroupForm({...groupForm, description: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none" rows={3} placeholder="Describe this group..." />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Color</label>
                <input type="color" value={groupForm.color} onChange={(e) => setGroupForm({...groupForm, color: e.target.value})} className="w-20 h-10 bg-zinc-800 border border-zinc-700 rounded-lg cursor-pointer" />
              </div>
              <div className="pt-4 flex gap-3">
                <Button onClick={() => setShowGroupModal(false)} variant="outline" className="flex-1 border-zinc-700">Cancel</Button>
                <Button onClick={saveGroup} className="flex-1 bg-emerald-500">Create Group</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMPanel;
